import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { cn } from '@/lib/utils';
import { ExecutionChatMessageList } from './ExecutionChatMessageList';
import { useExecutionChatState } from '@/lib/hooks/useExecutionChatState';
import { useStickyChatScroll } from '@/lib/hooks/useStickyChatScroll';
import type { FlowExecutionStatus } from '@shared/types/flow-executions';

interface ExecutionChatPanelProps {
  executionId: string;
  executionStatus: FlowExecutionStatus;
  className?: string;
  focusStepId?: string | null;
  focusRequestId?: number;
  toolResultHref?: string | null;
}

export const ExecutionChatPanel = ({
  executionId,
  executionStatus,
  className,
  focusStepId = null,
  focusRequestId = 0,
  toolResultHref = null,
}: ExecutionChatPanelProps) => {
  const [composerValue, setComposerValue] = useState('');
  const {
    messages,
    mode,
    composerState,
    blockedStep,
    availableActions,
    isLoading,
    isSubmitting,
    streamingText,
    streamingStatus,
    error,
    runAct,
    sendMessage,
  } = useExecutionChatState(executionId, executionStatus);
  const {
    scrollRef: listRef,
    handleScroll: handleListScroll,
    resumeAutoScroll,
  } = useStickyChatScroll<HTMLDivElement>({
    watch: [messages, isLoading, streamingText, streamingStatus],
  });

  const handleSendMessage = async (): Promise<void> => {
    const text = composerValue.trim();
    if (!text) return;
    resumeAutoScroll();
    const handled = await sendMessage(text);
    if (handled) {
      setComposerValue('');
    }
  };

  const effectiveComposerState =
    mode === 'read_only'
      ? {
          enabled: false,
          reason:
            'Exécution en cours. Les actions seront possibles quand le travail sera en pause.',
          unblockHint: 'Attendez une demande de validation pour interagir avec le chat.',
        }
      : composerState;
  const canSendMessage = effectiveComposerState.enabled && !isSubmitting;
  const activeBlockedStepId = blockedStep?.stepId ?? null;
  const isActionEnabled = mode === 'active';
  const hasComposerMessage = Boolean(
    effectiveComposerState.reason || effectiveComposerState.unblockHint
  );

  return (
    <Card className={cn('flex h-full min-h-[500px] flex-col overflow-hidden', className)}>
      {toolResultHref && (
        <div className="hidden justify-end border-b border-gray-100 px-4 py-3 xl:flex">
          <Link
            to={toolResultHref}
            className="inline-flex h-9 items-center justify-center rounded-lg border border-domino-cardBorder bg-domino-white px-3 text-sm font-medium text-domino-ink shadow-sm transition hover:border-domino-aubergine hover:bg-domino-muted"
          >
            Voir le résultat dans le tool
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </div>
      )}

      {activeBlockedStepId && (
        <div className="border-b border-gray-100 px-4 py-2 text-xs text-gray-600">
          Action requise pour continuer
          {availableActions.length > 0 && (
            <span className="ml-2 text-gray-400">
              Choix disponibles: {availableActions.join(', ')}
            </span>
          )}
        </div>
      )}

      <ExecutionChatMessageList
        messages={messages}
        isLoading={isLoading}
        isSubmitting={isSubmitting}
        isActionEnabled={isActionEnabled}
        streamingText={streamingText}
        streamingStatus={streamingStatus}
        listRef={listRef}
        focusStepId={focusStepId}
        focusRequestId={focusRequestId}
        onScroll={handleListScroll}
        onRunAct={async (actionId, args, options) => {
          resumeAutoScroll();
          return runAct(actionId, args, options);
        }}
      />

      <div className="border-t border-gray-100 bg-white p-3">
        {error && (
          <p className="mb-2 rounded-md bg-red-50 px-2 py-1 text-xs text-red-700">
            {error}
          </p>
        )}
        <div className="space-y-2">
          <Textarea
            value={composerValue}
            onChange={(event) => setComposerValue(event.target.value)}
            rows={3}
            placeholder={
              mode === 'active'
                ? 'Ex: utilise le contact de Bordeaux avec le téléphone'
                : 'Ex: pourquoi le travail est en pause ?'
            }
            disabled={!canSendMessage}
          />
          <div
            className={cn(
              'flex items-center',
              hasComposerMessage ? 'justify-between gap-3' : 'justify-end'
            )}
          >
            {hasComposerMessage && (
              <div>
                {effectiveComposerState.reason && (
                  <p className="text-[11px] text-gray-500">
                    {effectiveComposerState.reason}
                  </p>
                )}
                {effectiveComposerState.unblockHint && (
                  <p className="text-[11px] text-gray-400">
                    {effectiveComposerState.unblockHint}
                  </p>
                )}
              </div>
            )}
            <Button
              onClick={() => void handleSendMessage()}
              disabled={!canSendMessage || composerValue.trim().length < 1}
              loading={isSubmitting}
            >
              Envoyer
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};
